/**
 * BatchScheduleReport.js
 * Batch × Day-of-Week matrix with filters, capacity utilisation bars,
 * and a drill-down panel showing enrolled students per batch.
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  FaLayerGroup, FaUsers, FaFilter, FaChevronDown, FaChevronUp,
  FaTimes, FaCheckCircle, FaClock, FaExclamationTriangle,
} from "react-icons/fa";
import { Colors } from "./branding";
import { useTheme, getT, getBrandColors } from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import API_BASE from "./apiConfig";
import { DAY_NAMES_FULL, DAY_NAMES_SHORT } from "./constants/days";

const BASE = API_BASE;
const DAYS  = DAY_NAMES_SHORT;
const DAYS_FULL = DAY_NAMES_FULL;

const fmt12 = t => {
  if (!t) return "—";
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2,"0")} ${h >= 12 ? "PM" : "AM"}`;
};

const utilColor = pct => {
  if (pct >= 90) return "#f87171";
  if (pct >= 70) return "#fbbf24";
  return "#34d399";
};

// ── KPI Pill ──────────────────────────────────────────────────────────────────
function KPIPill({ label, value, sub, color, T }) {
  return (
    <div style={{
      flex: 1, minWidth: 140, padding: "14px 18px", borderRadius: 14,
      background: T.glass, border: T.glassBdr,
      boxShadow: T.glassSh,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.07em", color: T.textMuted, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 900, color: color || T.textPrimary,
        lineHeight: 1, marginBottom: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: T.textMuted }}>{sub}</div>}
    </div>
  );
}

// ── Utilisation bar ───────────────────────────────────────────────────────────
function UtilBar({ enrolled, capacity, T }) {
  const pct = capacity > 0 ? Math.min(100, Math.round(enrolled / capacity * 100)) : 0;
  const col  = utilColor(pct);
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11,
        color: T.textMuted, marginBottom: 3 }}>
        <span>{enrolled} / {capacity}</span>
        <span style={{ color: col, fontWeight: 700 }}>{pct}%</span>
      </div>
      <div style={{ height: 5, borderRadius: 3, background: T.isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)" }}>
        <div style={{ height: "100%", borderRadius: 3, width: `${pct}%`,
          background: col, transition: "width 0.4s" }} />
      </div>
    </div>
  );
}

// ── Student drill-down panel ──────────────────────────────────────────────────
function DrillDown({ batch, onClose, T, isDark }) {
  const lc = batch.color_hex || "#3b82f6";
  const pct = batch.max_capacity > 0
    ? Math.min(100, Math.round(batch.enrolled / batch.max_capacity * 100)) : 0;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 9999, padding: 20 }}>
      <div style={{ background: isDark ? "#1e293b" : "#fff", borderRadius: 16,
        width: 640, maxHeight: "88vh", overflowY: "auto", padding: 28,
        boxShadow: "0 24px 80px rgba(0,0,0,0.4)" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 7,
              background: `${lc}18`, padding: "3px 12px", borderRadius: 20, marginBottom: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: lc }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: lc }}>{batch.level_name}</span>
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: T.textPrimary }}>{batch.batch_name}</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4, display: "flex", gap: 16 }}>
              <span>⏰ {fmt12(batch.weekday_start)} – {fmt12(batch.weekday_end)}</span>
              {batch.saturday_start && <span>Sat: {fmt12(batch.saturday_start)} – {fmt12(batch.saturday_end)}</span>}
              <span>📋 {batch.program_name}</span>
            </div>
          </div>
          <FaTimes style={{ cursor: "pointer", color: T.textMuted, fontSize: 18, marginTop: 2 }}
            onClick={onClose} />
        </div>

        {/* Days chips */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
          {DAYS.map((d, i) => (
            <span key={i} style={{
              padding: "3px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
              background: batch.schedule_days.includes(i)
                ? `${lc}20` : (isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"),
              color: batch.schedule_days.includes(i) ? lc : T.textMuted,
              border: `1px solid ${batch.schedule_days.includes(i) ? lc + "44" : "transparent"}`,
            }}>{d}</span>
          ))}
        </div>

        {/* Capacity bar */}
        <div style={{ padding: "12px 16px", borderRadius: 10,
          background: isDark ? "rgba(255,255,255,0.04)" : "rgba(3,78,123,0.04)",
          marginBottom: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: T.textPrimary }}>Capacity</span>
            <span style={{ fontSize: 12, fontWeight: 800, color: utilColor(pct) }}>{pct}% full</span>
          </div>
          <div style={{ height: 8, borderRadius: 4, background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)" }}>
            <div style={{ height: "100%", borderRadius: 4, width: `${pct}%`,
              background: utilColor(pct), transition: "width 0.4s" }} />
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 5 }}>
            {batch.enrolled} enrolled · {batch.max_capacity - batch.enrolled} seats available
          </div>
        </div>

        {/* Student list */}
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase",
          letterSpacing: "0.07em", color: T.textMuted, marginBottom: 10 }}>
          Enrolled Students ({batch.students?.length || 0})
        </div>

        {!batch.students?.length ? (
          <div style={{ textAlign: "center", padding: "32px 0", color: T.textMuted, fontSize: 13 }}>
            No students enrolled in this batch.
          </div>
        ) : (
          <div style={{ border: T.glassBdr, borderRadius: 10, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: isDark ? "rgba(255,255,255,0.05)" : "rgba(3,78,123,0.06)" }}>
                  {["#","Student","ID","Level"].map(h => (
                    <th key={h} style={{ padding: "8px 12px", textAlign: "left",
                      fontSize: 10, fontWeight: 800, textTransform: "uppercase",
                      letterSpacing: "0.07em", color: T.textMuted }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {batch.students.map((s, i) => (
                  <tr key={s.student_id}
                    style={{ borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(3,78,123,0.06)"}`,
                      background: i % 2 === 0 ? "transparent" : (isDark ? "rgba(255,255,255,0.02)" : "rgba(3,78,123,0.02)") }}>
                    <td style={{ padding: "8px 12px", color: T.textMuted, fontSize: 12 }}>{i + 1}</td>
                    <td style={{ padding: "8px 12px", fontWeight: 600, color: T.textPrimary }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 28, height: 28, borderRadius: "50%",
                          background: `${lc}28`, display: "flex", alignItems: "center",
                          justifyContent: "center", fontSize: 10, fontWeight: 700, color: lc, flexShrink: 0 }}>
                          {s.student_name?.split(" ").map(w => w[0]).slice(0,2).join("")}
                        </div>
                        {s.student_name}
                      </div>
                    </td>
                    <td style={{ padding: "8px 12px", color: T.textMuted, fontFamily: "monospace", fontSize: 11 }}>
                      {s.student_code || `#${s.student_id}`}
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      {s.student_level ? (
                        <span style={{ padding: "2px 9px", borderRadius: 12, fontSize: 10, fontWeight: 700,
                          color: s.student_level_color || "#6b7280",
                          background: (s.student_level_color || "#6b7280") + "18" }}>
                          {s.student_level}
                        </span>
                      ) : <span style={{ color: T.textMuted }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Batch card (used in both matrix and list views) ───────────────────────────
function BatchCard({ batch, onClick, T, isDark }) {
  const lc  = batch.color_hex || "#3b82f6";
  const pct = batch.max_capacity > 0
    ? Math.min(100, Math.round(batch.enrolled / batch.max_capacity * 100)) : 0;

  return (
    <div onClick={onClick} style={{
      padding: "10px 12px", borderRadius: 10, cursor: "pointer",
      border: `1px solid ${lc}33`,
      background: isDark ? `${lc}0d` : `${lc}08`,
      transition: "all 0.15s",
    }}
      onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
      onMouseLeave={e => e.currentTarget.style.transform = "none"}>
      <div style={{ fontSize: 12, fontWeight: 700, color: T.textPrimary,
        marginBottom: 4, lineHeight: 1.3 }}>{batch.batch_name}</div>
      <div style={{ fontSize: 10, color: lc, fontWeight: 700, marginBottom: 6 }}>
        {batch.level_name}
      </div>
      <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 6 }}>
        {fmt12(batch.weekday_start)} – {fmt12(batch.weekday_end)}
      </div>
      <UtilBar enrolled={batch.enrolled} capacity={batch.max_capacity} T={{ ...T, isDark }} />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════════
export default function BatchScheduleReport() {
  const { isDark } = useTheme();
  const T = { ...getT(isDark), isDark };

  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);

  // Filters
  const [filterProgram,  setFilterProgram]  = useState("");
  const [filterLevel,    setFilterLevel]    = useState("");
  const [filterDay,      setFilterDay]      = useState("");
  const [filterUtil,     setFilterUtil]     = useState(""); // "low" <50 | "mid" 50-89 | "high" >=90
  const [filterSearch,   setFilterSearch]   = useState("");
  const [showFilters,    setShowFilters]    = useState(true);

  // View mode
  const [viewMode,    setViewMode]    = useState("matrix"); // "matrix" | "list"

  // Drill-down
  const [drillBatch,  setDrillBatch]  = useState(null);

  const buildUrl = useCallback(() => {
    const p = new URLSearchParams();
    if (filterProgram) p.append("program_id", filterProgram);
    if (filterLevel)   p.append("level_id",   filterLevel);
    if (filterDay !== "") p.append("day_of_week", filterDay);
    return `${BASE}/batches/schedule-report?${p}`;
  }, [filterProgram, filterLevel, filterDay]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(buildUrl());
      const d   = await res.json();
      if (d.success) setData(d);
      else setError("Failed to load report.");
    } catch { setError("Network error — please check the server."); }
    setLoading(false);
  }, [buildUrl]);

  useEffect(() => { load(); }, [load]);

  // Client-side filters (util + search)
  const visibleBatches = (data?.batches || []).filter(b => {
    const pct = b.max_capacity > 0 ? b.enrolled / b.max_capacity * 100 : 0;
    if (filterUtil === "low"  && pct >= 50) return false;
    if (filterUtil === "mid"  && (pct < 50 || pct >= 90)) return false;
    if (filterUtil === "high" && pct < 90) return false;
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      if (!b.batch_name.toLowerCase().includes(q) &&
          !(b.level_name||"").toLowerCase().includes(q) &&
          !(b.program_name||"").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const inp = {
    padding: "7px 11px", borderRadius: 8, fontSize: 12, outline: "none",
    background: T.inputBg, color: T.inputText, border: `1px solid ${T.inputBdr}`,
  };
  const btn = (active) => ({
    padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer",
    fontSize: 12, fontWeight: 700,
    background: active ? Colors.primaryBlue : (isDark ? "rgba(255,255,255,0.08)" : "rgba(3,78,123,0.08)"),
    color: active ? "#fff" : T.textSub,
  });

  const totals = data?.totals || {};
  const overallPct = totals.capacity > 0
    ? Math.round(totals.enrolled / totals.capacity * 100) : 0;

  return (
    <div style={{ padding: "18px 22px", fontFamily: "'Segoe UI', system-ui, sans-serif",
      maxWidth: 1400, margin: "0 auto" }}>

      {/* ── Title bar ──────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <PageHeaderIcon icon={<FaLayerGroup size={18} color={getBrandColors().blueDark} />} />
          <div>
            <div style={{ fontSize: 20, fontWeight: 900, color: T.textPrimary }}>Batch Schedule Report</div>
            <div style={{ fontSize: 12, color: T.textMuted }}>Capacity · Enrolment · Day-by-Day view</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={btn(viewMode === "matrix")} onClick={() => setViewMode("matrix")}>⊞ Matrix</button>
          <button style={btn(viewMode === "list")}   onClick={() => setViewMode("list")}>≡ List</button>
          <button onClick={load} style={{ ...btn(false), opacity: loading ? 0.5 : 1 }}>
            {loading ? "⟳ Loading…" : "↻ Refresh"}
          </button>
          <button style={btn(false)} onClick={() => setShowFilters(v => !v)}>
            <FaFilter size={11} style={{ marginRight: 5 }} />
            {showFilters ? "Hide Filters" : "Show Filters"}
          </button>
        </div>
      </div>

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      {showFilters && (
        <div style={{ padding: "14px 18px", borderRadius: 12, marginBottom: 18,
          background: T.glass, border: T.glassBdr,
          display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, marginBottom: 4, textTransform: "uppercase" }}>Program</div>
            <select value={filterProgram} onChange={e => setFilterProgram(e.target.value)} style={inp}>
              <option value="">All Programs</option>
              {(data?.programs || []).map(p => (
                <option key={p.program_id} value={p.program_id}>{p.program_name}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, marginBottom: 4, textTransform: "uppercase" }}>Skill Level</div>
            <select value={filterLevel} onChange={e => setFilterLevel(e.target.value)} style={inp}>
              <option value="">All Levels</option>
              {(data?.levels || []).map(l => (
                <option key={l.level_id} value={l.level_id}>{l.level_name}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, marginBottom: 4, textTransform: "uppercase" }}>Day of Week</div>
            <select value={filterDay} onChange={e => setFilterDay(e.target.value)} style={inp}>
              <option value="">All Days</option>
              {DAYS.map((d, i) => <option key={i} value={i}>{DAYS_FULL[i]}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, marginBottom: 4, textTransform: "uppercase" }}>Utilisation</div>
            <select value={filterUtil} onChange={e => setFilterUtil(e.target.value)} style={inp}>
              <option value="">Any</option>
              <option value="low">Low (&lt;50%)</option>
              <option value="mid">Medium (50–89%)</option>
              <option value="high">High (≥90%)</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, marginBottom: 4, textTransform: "uppercase" }}>Search</div>
            <input placeholder="Batch name, level, program…" value={filterSearch}
              onChange={e => setFilterSearch(e.target.value)} style={{ ...inp, width: 200 }} />
          </div>
          {(filterProgram || filterLevel || filterDay !== "" || filterUtil || filterSearch) && (
            <button onClick={() => { setFilterProgram(""); setFilterLevel(""); setFilterDay(""); setFilterUtil(""); setFilterSearch(""); }}
              style={{ ...btn(false), display: "flex", alignItems: "center", gap: 6, color: "#f87171" }}>
              <FaTimes size={10} /> Clear
            </button>
          )}
        </div>
      )}

      {/* ── KPI row ────────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        <KPIPill label="Active Batches"   value={totals.batch_count ?? "—"} T={T} color={Colors.primaryBlue} />
        <KPIPill label="Total Enrolled"   value={totals.enrolled ?? "—"}     T={T} color="#34d399"
          sub={totals.enrolled != null
            ? `${totals.enrolled_active ?? 0} Active · ${totals.enrolled_suspended ?? 0} Suspended`
              + (totals.enrolled_other ? ` · ${totals.enrolled_other} Other` : "")
            : undefined} />
        <KPIPill label="Total Capacity"   value={totals.capacity ?? "—"}     T={T} color={T.textPrimary} />
        <KPIPill label="Overall Utilisation" value={data ? `${overallPct}%` : "—"}
          sub={`${totals.enrolled} of ${totals.capacity} seats`}
          T={T} color={utilColor(overallPct)} />
        <KPIPill label="Training Days"    value={totals.unique_days ?? "—"}  T={T} color="#a78bfa" />
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: "12px 16px", borderRadius: 10, marginBottom: 16,
          background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.25)",
          color: "#f87171", display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
          <FaExclamationTriangle /> {error}
        </div>
      )}

      {loading && !data && (
        <div style={{ textAlign: "center", padding: "60px 0", color: T.textMuted }}>Loading…</div>
      )}

      {data && viewMode === "matrix" && (
        <MatrixView batches={visibleBatches} daySummary={data.day_summary}
          filterDay={filterDay} onDrill={setDrillBatch} T={T} isDark={isDark} />
      )}

      {data && viewMode === "list" && (
        <ListView batches={visibleBatches} onDrill={setDrillBatch} T={T} isDark={isDark} />
      )}

      {drillBatch && (
        <DrillDown batch={drillBatch} onClose={() => setDrillBatch(null)} T={T} isDark={isDark} />
      )}
    </div>
  );
}

// ── Matrix View ───────────────────────────────────────────────────────────────
function MatrixView({ batches, daySummary, filterDay, onDrill, T, isDark }) {
  const activeDays = filterDay !== ""
    ? [parseInt(filterDay)]
    : [0,1,2,3,4,5,6].filter(i => (daySummary[i]?.batch_count || 0) > 0);

  if (!activeDays.length) {
    return (
      <div style={{ textAlign: "center", padding: "60px 0", color: T.textMuted, fontSize: 14 }}>
        No batches match the current filters.
      </div>
    );
  }

  const thSt = {
    padding: "10px 14px", textAlign: "center", fontWeight: 800, fontSize: 11,
    textTransform: "uppercase", letterSpacing: "0.07em",
    color: T.textPrimary, borderBottom: `2px solid ${isDark ? "rgba(255,255,255,0.10)" : "rgba(3,78,123,0.14)"}`,
    whiteSpace: "nowrap",
  };

  return (
    <div>
      {/* Day summary header row */}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${activeDays.length}, 1fr)`,
        gap: 10, marginBottom: 14 }}>
        {activeDays.map(d => {
          const ds = daySummary[d] || {};
          const pct = ds.capacity > 0 ? Math.round(ds.enrolled / ds.capacity * 100) : 0;
          return (
            <div key={d} style={{ padding: "12px 14px", borderRadius: 12,
              background: T.glass, border: T.glassBdr, textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: T.textPrimary, marginBottom: 2 }}>
                {DAYS_FULL[d]}
              </div>
              <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>
                {ds.batch_count || 0} batch{ds.batch_count !== 1 ? "es" : ""}
              </div>
              <div style={{ fontSize: 18, fontWeight: 900, color: utilColor(pct), marginBottom: 4 }}>
                {ds.enrolled || 0}
              </div>
              <div style={{ fontSize: 10, color: T.textMuted }}>students · {pct}% util</div>
            </div>
          );
        })}
      </div>

      {/* Batch cards grid */}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${activeDays.length}, 1fr)`,
        gap: 10, alignItems: "start" }}>
        {activeDays.map(d => {
          const dayBatches = batches.filter(b => b.schedule_days.includes(d));
          return (
            <div key={d} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {dayBatches.length === 0 ? (
                <div style={{ padding: "20px 12px", borderRadius: 10, textAlign: "center",
                  color: T.textMuted, fontSize: 12,
                  border: `1px dashed ${isDark ? "rgba(255,255,255,0.10)" : "rgba(3,78,123,0.12)"}` }}>
                  No batches
                </div>
              ) : dayBatches.map(b => (
                <BatchCard key={b.batch_id} batch={b} onClick={() => onDrill(b)} T={T} isDark={isDark} />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── List View ─────────────────────────────────────────────────────────────────
function ListView({ batches, onDrill, T, isDark }) {
  const [sortKey,  setSortKey]  = useState("level_order");
  const [sortDir,  setSortDir]  = useState("asc");
  const [expanded, setExpanded] = useState({});

  const toggleSort = key => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const sortValue = (row, key) => {
    if (key === "days_count") return row.schedule_days?.length || 0;
    if (key === "utilization") return row.max_capacity > 0 ? row.enrolled / row.max_capacity : -1;
    return row[key];
  };

  const sorted = [...batches].sort((a, b) => {
    let va = sortValue(a, sortKey); let vb = sortValue(b, sortKey);
    if (typeof va === "string") va = va.toLowerCase();
    if (typeof vb === "string") vb = vb.toLowerCase();
    if (va == null) va = -Infinity;
    if (vb == null) vb = -Infinity;
    return sortDir === "asc" ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
  });

  // Every sortable header needs a visible affordance, not just the active
  // one — a bare cursor:pointer is invisible until hovered.
  const sortIcon = key => sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : " ⇅";
  const thSt = key => ({
    padding: "10px 14px", textAlign: "left", fontWeight: 800, fontSize: 10,
    textTransform: "uppercase", letterSpacing: "0.07em",
    color: sortKey === key ? Colors.primaryBlue : T.textMuted,
    borderBottom: `1.5px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(3,78,123,0.10)"}`,
    cursor: "pointer", whiteSpace: "nowrap", userSelect: "none",
  });
  const tdSt = { padding: "10px 14px", borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(3,78,123,0.06)"}`, };

  if (!sorted.length) return (
    <div style={{ textAlign: "center", padding: "60px 0", color: T.textMuted }}>No batches match the current filters.</div>
  );

  return (
    <div style={{ background: T.glass, border: T.glassBdr, borderRadius: 14, overflow: "hidden" }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: isDark ? "rgba(255,255,255,0.03)" : "rgba(3,78,123,0.04)" }}>
              <th style={thSt("batch_name")}    onClick={() => toggleSort("batch_name")}>
                Batch {sortIcon("batch_name")}
              </th>
              <th style={thSt("program_name")}  onClick={() => toggleSort("program_name")}>
                Program {sortIcon("program_name")}
              </th>
              <th style={thSt("level_order")}   onClick={() => toggleSort("level_order")}>
                Skill Level {sortIcon("level_order")}
              </th>
              <th style={thSt("days_count")} onClick={() => toggleSort("days_count")}>
                Days {sortIcon("days_count")}
              </th>
              <th style={thSt("weekday_start")} onClick={() => toggleSort("weekday_start")}>
                Time {sortIcon("weekday_start")}
              </th>
              <th style={thSt("enrolled")} onClick={() => toggleSort("enrolled")}>
                Enrolled {sortIcon("enrolled")}
              </th>
              <th style={thSt("max_capacity")} onClick={() => toggleSort("max_capacity")}>
                Capacity {sortIcon("max_capacity")}
              </th>
              <th style={{ ...thSt("utilization"), minWidth: 140 }} onClick={() => toggleSort("utilization")}>
                Utilisation {sortIcon("utilization")}
              </th>
              <th style={{ ...thSt(""), cursor: "default" }}></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((b, i) => {
              const lc  = b.color_hex || "#3b82f6";
              const pct = b.max_capacity > 0
                ? Math.min(100, Math.round(b.enrolled / b.max_capacity * 100)) : 0;
              const isExp = expanded[b.batch_id];

              return (
                <React.Fragment key={b.batch_id}>
                  <tr style={{ background: i % 2 === 0 ? "transparent"
                    : (isDark ? "rgba(255,255,255,0.02)" : "rgba(3,78,123,0.02)") }}>
                    <td style={{ ...tdSt, fontWeight: 700, color: T.textPrimary }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: lc, flexShrink: 0 }} />
                        {b.batch_name}
                      </div>
                    </td>
                    <td style={{ ...tdSt, color: T.textSub }}>{b.program_name || "—"}</td>
                    <td style={tdSt}>
                      <span style={{ padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700,
                        color: lc, background: `${lc}18` }}>{b.level_name}</span>
                    </td>
                    <td style={{ ...tdSt, whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {DAYS.map((d, di) => (
                          <span key={di} style={{
                            padding: "1px 7px", borderRadius: 8, fontSize: 10, fontWeight: 700,
                            background: b.schedule_days.includes(di)
                              ? `${lc}20` : (isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"),
                            color: b.schedule_days.includes(di) ? lc : T.textMuted,
                          }}>{d}</span>
                        ))}
                      </div>
                    </td>
                    <td style={{ ...tdSt, color: T.textSub, whiteSpace: "nowrap", fontSize: 12 }}>
                      {fmt12(b.weekday_start)} – {fmt12(b.weekday_end)}
                      {b.saturday_start && (
                        <div style={{ color: T.textMuted, fontSize: 10 }}>
                          Sat: {fmt12(b.saturday_start)} – {fmt12(b.saturday_end)}
                        </div>
                      )}
                    </td>
                    <td style={{ ...tdSt, textAlign: "center", fontWeight: 800,
                      color: T.textPrimary, fontSize: 15 }}>{b.enrolled}</td>
                    <td style={{ ...tdSt, textAlign: "center", color: T.textMuted }}>{b.max_capacity}</td>
                    <td style={{ ...tdSt, minWidth: 140 }}>
                      <UtilBar enrolled={b.enrolled} capacity={b.max_capacity} T={{ ...T, isDark }} />
                    </td>
                    <td style={{ ...tdSt, whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => onDrill(b)}
                          style={{ padding: "4px 10px", borderRadius: 6, border: "none",
                            cursor: "pointer", fontSize: 11, fontWeight: 700,
                            background: Colors.primaryBlue, color: "#fff" }}>
                          <FaUsers size={10} style={{ marginRight: 4 }} />Students
                        </button>
                        <button onClick={() => setExpanded(e => ({ ...e, [b.batch_id]: !e[b.batch_id] }))}
                          style={{ padding: "4px 8px", borderRadius: 6, border: T.glassBdr,
                            cursor: "pointer", fontSize: 11, background: "transparent",
                            color: T.textSub }}>
                          {isExp ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isExp && (
                    <tr>
                      <td colSpan={9} style={{ padding: "0 14px 12px 28px",
                        borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(3,78,123,0.06)"}`,
                        background: isDark ? "rgba(255,255,255,0.01)" : "rgba(3,78,123,0.01)" }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted,
                          textTransform: "uppercase", letterSpacing: "0.06em", margin: "8px 0 6px" }}>
                          Students ({b.students?.length || 0})
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {b.students?.length === 0
                            ? <span style={{ fontSize: 12, color: T.textMuted }}>No students enrolled.</span>
                            : b.students.map(s => (
                              <span key={s.student_id} style={{
                                padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                                background: `${lc}14`, color: T.textSub,
                                border: `1px solid ${lc}22`,
                              }}>
                                {s.student_name}
                                {s.student_code && <span style={{ marginLeft: 4, color: T.textMuted }}>#{s.student_code}</span>}
                              </span>
                            ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ padding: "10px 14px", borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(3,78,123,0.06)"}`,
        fontSize: 11, color: T.textMuted }}>
        Showing {sorted.length} batch{sorted.length !== 1 ? "es" : ""}
      </div>
    </div>
  );
}
